<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.platformSkills.editSkill.title') }}</h2>
        <button @click="$emit('close')" class="btn-close">✕</button>
      </div>

      <div v-if="loadingDetail" class="modal-body state-msg">{{ $t('bo.common.loading') }}</div>

      <form v-else @submit.prevent="handleSubmit" class="modal-form">
        <div class="modal-body">
          <div class="field">
            <label>{{ $t('bo.common.name') }} <span class="required">*</span></label>
            <input v-model="form.name" type="text" required />
          </div>

          <div class="field">
            <label>{{ $t('bo.common.description') }}</label>
            <textarea v-model="form.description" rows="2" />
          </div>

          <!-- Traduction francaise (skills_i18n) : affichee dans l'application en francais. -->
          <div class="translation-box">
            <div class="translation-head">
              <span class="translation-title">{{ $t('bo.platformSkills.translation.title') }}</span>
              <button type="button" class="btn-translate" :disabled="translating" @click="autoTranslate">
                {{ translating ? $t('bo.platformSkills.translation.translating') : $t('bo.platformSkills.translation.auto') }}
              </button>
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.translation.nameFr') }}</label>
              <input v-model="translationFr.name" type="text" maxlength="100" />
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.translation.descriptionFr') }}</label>
              <textarea v-model="translationFr.description" rows="2" />
            </div>
            <p class="hint">{{ translateMessage || $t('bo.platformSkills.translation.hint') }}</p>
          </div>

          <div class="field-row">
            <div class="field">
              <label>{{ $t('bo.platformSkills.editSkill.skillGroups') }} <span class="required">*</span></label>
              <select v-model="form.skill_group_ids" multiple required class="select-multi">
                <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
              </select>
              <span class="hint">{{ $t('bo.platformSkills.editSkill.ctrlClickHint') }}</span>
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.colType') }}</label>
              <select v-model="form.skill_type">
                <option value="">{{ $t('bo.common.none') }}</option>
                <option v-for="st in skillTypes" :key="st.code" :value="st.code">{{ st.label }}</option>
              </select>
            </div>
          </div>

          <div class="field-row">
            <div class="field">
              <label>{{ $t('bo.platformSkills.colKind') }}</label>
              <select v-model="form.atom_kind">
                <option value="skill">{{ $t('bo.platformSkills.skillKind.skill') }}</option>
                <option value="tool">{{ $t('bo.platformSkills.skillKind.tool') }}</option>
                <option value="certification">{{ $t('bo.platformSkills.skillKind.certification') }}</option>
                <option value="framework">{{ $t('bo.platformSkills.skillKind.framework') }}</option>
              </select>
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.colLevel') }}</label>
              <select v-model="form.level">
                <option value="L2">{{ $t('bo.platformSkills.skillLevel.L2') }}</option>
                <option value="L3">{{ $t('bo.platformSkills.skillLevel.L3') }}</option>
                <option value="L0">{{ $t('bo.platformSkills.skillLevel.L0') }}</option>
              </select>
            </div>
          </div>

          <div v-if="form.atom_kind === 'tool'" class="field">
            <label>{{ $t('bo.platformSkills.createSkill.iconUrl') }}</label>
            <input
              v-model="form.icon_url"
              type="url"
              maxlength="512"
              :placeholder="$t('bo.platformSkills.createSkill.iconUrlPlaceholder')"
            />
            <span class="hint">{{ $t('bo.platformSkills.createSkill.iconUrlHint') }}</span>
            <div v-if="form.icon_url" class="icon-preview">
              <img :src="form.icon_url" alt="" @error="onIconError" />
            </div>
          </div>

          <div class="field">
            <label>{{ $t('bo.common.status') }}</label>
            <input :value="detail?.status || ''" disabled class="input-readonly" />
          </div>

          <div class="field">
            <label>{{ $t('bo.platformSkills.createSkill.aliases') }} <span class="hint">{{ $t('bo.platformSkills.createSkill.aliasesHint') }}</span></label>
            <input v-model="aliasesInput" type="text" :placeholder="$t('bo.platformSkills.createSkill.aliasesPlaceholder')" />
          </div>

          <div class="field">
            <label>{{ $t('bo.platformSkills.colSlug') }}</label>
            <input :value="detail?.slug || '—'" disabled class="input-readonly" />
          </div>

          <!-- ESCO Mappings (editable) -->
          <div class="section-title">{{ $t('bo.platformSkills.editSkill.escoMappings') }}</div>

          <div class="esco-section">
            <div v-if="selectedEscoSkills.length" class="esco-chips">
              <span v-for="es in selectedEscoSkills" :key="es.id" class="esco-chip">
                {{ es.title }}
                <button type="button" class="chip-remove" @click="removeEscoSkill(es.id)">×</button>
              </span>
            </div>
            <div v-else class="esco-empty">{{ $t('bo.platformSkills.editSkill.currentlyMapped', { count: 0 }) }}</div>

            <div class="esco-controls">
              <select v-model="escoGroup" @change="onEscoGroupChange" class="esco-group-select">
                <option value="">{{ $t('bo.platformSkills.editSkill.escoSelectGroup') }}</option>
                <option v-for="g in escoGroups" :key="g.group_key" :value="g.group_key">{{ g.display_name }}</option>
              </select>

              <div v-if="escoGroup" class="esco-search-wrapper">
                <input
                  v-model="escoQuery"
                  type="text"
                  :placeholder="$t('bo.platformSkills.createSkill.escoSearchPlaceholder')"
                  @input="onEscoInput"
                  @blur="hideDropdownDelayed"
                  autocomplete="off"
                />
                <div v-if="escoDropdownOpen" class="esco-dropdown">
                  <div v-if="escoSearching" class="esco-dropdown-msg">{{ $t('bo.platformSkills.createSkill.escoSearching') }}</div>
                  <div v-else-if="!escoResults.length" class="esco-dropdown-msg">{{ $t('bo.platformSkills.createSkill.escoNoResults') }}</div>
                  <div
                    v-else
                    v-for="r in escoResults"
                    :key="r.id"
                    class="esco-result-item"
                    :class="{ 'already-selected': isEscoSelected(r.id) }"
                    @mousedown.prevent="addEscoSkill(r)"
                  >
                    {{ r.title }}
                    <span v-if="isEscoSelected(r.id)" class="esco-check">✓</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="error" class="error-msg">{{ error }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="saving">
            {{ saving ? $t('bo.common.saving') : $t('bo.common.saveChanges') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import platformSkillService from '@/services/platformSkillService'
import escoService from '@/services/escoService'

const { t } = useI18n()

const props = defineProps({
  skill: { type: Object, required: true },
  groups: { type: Array, required: true },
})

const emit = defineEmits(['close', 'updated'])

const detail = ref(null)
const loadingDetail = ref(false)
const saving = ref(false)
const error = ref('')
const skillTypes = ref([])

const form = ref({
  name: '',
  description: '',
  skill_group_ids: [],
  skill_type: '',
  atom_kind: 'skill',
  level: 'L2',
  icon_url: '',
})
const aliasesInput = ref('')
// Traduction francaise du skill (envoyee dans translations.fr a l'enregistrement).
const translationFr = ref({ name: '', description: '' })
const translating = ref(false)
const translateMessage = ref('')

// ESCO linking
const escoQuery = ref('')
const escoResults = ref([])
const escoSearching = ref(false)
const escoDropdownOpen = ref(false)
const selectedEscoSkills = ref([])
const escoGroups = ref([])
const escoGroup = ref('')
let escoTimer = null

const onEscoGroupChange = () => {
  escoQuery.value = ''
  escoResults.value = []
  escoDropdownOpen.value = false
}

const onEscoInput = () => {
  clearTimeout(escoTimer)
  const q = escoQuery.value.trim()
  if (!escoGroup.value || !q) {
    escoResults.value = []
    escoDropdownOpen.value = false
    return
  }
  escoDropdownOpen.value = true
  escoSearching.value = true
  escoTimer = setTimeout(async () => {
    try {
      const res = await escoService.getGroupSkills(escoGroup.value, { search: q, per_page: 20 })
      escoResults.value = (res.data?.data || []).map(s => ({ id: s.id, title: s.title }))
    } catch {
      escoResults.value = []
    } finally {
      escoSearching.value = false
    }
  }, 300)
}

const hideDropdownDelayed = () => {
  setTimeout(() => { escoDropdownOpen.value = false }, 150)
}

const isEscoSelected = (id) => selectedEscoSkills.value.some(s => s.id === id)

const addEscoSkill = (skill) => {
  if (!isEscoSelected(skill.id)) {
    selectedEscoSkills.value.push(skill)
  }
  escoQuery.value = ''
  escoResults.value = []
  escoDropdownOpen.value = false
}

const removeEscoSkill = (id) => {
  selectedEscoSkills.value = selectedEscoSkills.value.filter(s => s.id !== id)
}

const onIconError = (e) => {
  e.target.style.display = 'none'
}

onMounted(async () => {
  loadingDetail.value = true

  escoService.getSkillGroupsList().then(res => {
    escoGroups.value = (res.data || []).sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''))
  }).catch(() => {})

  await Promise.all([
    platformSkillService.getSkillTypes().then((res) => {
      skillTypes.value = res.data || []
    }).catch(() => {}),
    platformSkillService.getSkill(props.skill.id).then((res) => {
      detail.value = res.data
    }).catch(() => {
      detail.value = props.skill
    }),
  ])

  const src = detail.value
  form.value = {
    name: src.name,
    description: src.description || '',
    skill_group_ids: (src.skill_groups || []).map(g => g.id),
    skill_type: src.skill_type || '',
    atom_kind: src.atom_kind || 'skill',
    level: src.level || 'L2',
    icon_url: src.icon_url || '',
  }
  aliasesInput.value = (src.aliases || []).join(', ')
  translationFr.value = {
    name: src.translations?.fr?.name || '',
    description: src.translations?.fr?.description || '',
  }

  selectedEscoSkills.value = (src.esco_mappings || []).map(m => ({
    id: m.id,
    title: m.title_en || m.title_fr || m.title || String(m.id),
  }))

  loadingDetail.value = false
})

// Suggestion LibreTranslate a partir du nom et de la description enregistres : l'admin relit.
const autoTranslate = async () => {
  translating.value = true
  translateMessage.value = ''
  try {
    const res = await platformSkillService.autoTranslateSkill(props.skill.id)
    const suggestion = res.data || {}
    if (!suggestion.available) {
      translateMessage.value = t('bo.platformSkills.translation.unavailable')
      return
    }
    translationFr.value = {
      name: suggestion.name || translationFr.value.name,
      description: suggestion.description || translationFr.value.description,
    }
    translateMessage.value = t('bo.platformSkills.translation.review')
  } catch {
    translateMessage.value = t('bo.platformSkills.translation.unavailable')
  } finally {
    translating.value = false
  }
}

const handleSubmit = async () => {
  saving.value = true
  error.value = ''
  try {
    const aliases = aliasesInput.value
      ? aliasesInput.value.split(',').map((s) => s.trim()).filter(Boolean)
      : []
    await platformSkillService.updateSkill(props.skill.id, {
      ...form.value,
      skill_type: form.value.skill_type || null,
      skill_group_ids: form.value.skill_group_ids,
      aliases,
      translations: {
        fr: { name: translationFr.value.name.trim(), description: translationFr.value.description.trim() },
      },
      icon_url: form.value.atom_kind === 'tool' && form.value.icon_url.trim()
        ? form.value.icon_url.trim()
        : null,
    })
    await platformSkillService.syncSkillEscoMapping(
      props.skill.id,
      selectedEscoSkills.value.map(s => s.id)
    )
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.common.failedSave')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.125rem;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
}

.btn-close:hover { color: #374151; }

.modal-form { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.modal-body { padding: 1.5rem; overflow-y: auto; flex: 1; }

.state-msg { text-align: center; color: #6b7280; padding: 2rem; }

.field { margin-bottom: 1.25rem; flex: 1; }

.field-row { display: flex; gap: 1rem; }

.field label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
}

.required { color: #ef4444; }
.hint { font-weight: 400; color: #9ca3af; font-size: 0.8rem; }

.field input,
.field select,
.field textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  background: white;
  box-sizing: border-box;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.input-readonly { background: #f9fafb; color: #6b7280; }

.select-multi { min-height: 120px; }

.hint { display: block; font-size: 0.75rem; color: #9ca3af; margin-top: 0.25rem; }

.field textarea { resize: vertical; }

.icon-preview {
  margin-top: 0.5rem;
}

.icon-preview img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #f9fafb;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.esco-section { margin-bottom: 1.25rem; }

.esco-empty {
  font-size: 0.8rem;
  color: #9ca3af;
  font-style: italic;
  margin-bottom: 0.5rem;
}

/* ESCO chips */
.esco-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
}

.esco-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  border-radius: 9999px;
}

.chip-remove {
  background: none;
  border: none;
  color: #1d4ed8;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.chip-remove:hover { color: #1e3a8a; }

/* ESCO group + search controls */
.esco-controls { display: flex; flex-direction: column; gap: 0.5rem; }

.esco-group-select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  background: white;
  box-sizing: border-box;
}

.esco-group-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

/* ESCO search */
.esco-search-wrapper { position: relative; }

.esco-search-wrapper input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  background: white;
  box-sizing: border-box;
}

.esco-search-wrapper input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.esco-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 50;
  max-height: 200px;
  overflow-y: auto;
}

.esco-dropdown-msg {
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
}

.esco-result-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #1f2937;
  cursor: pointer;
}

.esco-result-item:hover { background: #f3f4f6; }
.esco-result-item.already-selected { color: #6b7280; }

.esco-check { color: #22c55e; font-weight: 700; }

.error-msg {
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-primary {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.translation-box { border: 1px solid #e5e7eb; border-radius: 0.75rem; padding: 0.9rem 1rem; background: #fafafa; display: flex; flex-direction: column; gap: 0.6rem; }
.translation-head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; }
.translation-title { font-weight: 600; color: #1f2937; font-size: 0.9rem; }
.btn-translate { border: 1px solid #3b82f6; color: #3b82f6; background: #fff; border-radius: 0.5rem; padding: 0.35rem 0.8rem; font-size: 0.8rem; cursor: pointer; }
.btn-translate:disabled { opacity: 0.6; cursor: default; }
.translation-box .hint { margin: 0; color: #6b7280; font-size: 0.8rem; }
</style>
